import CloseIcon from '@mui/icons-material/Close';
import Autocomplete from '@mui/material/Autocomplete';
import Button from '@mui/material/Button';
import Checkbox from '@mui/material/Checkbox';
import CircularProgress from '@mui/material/CircularProgress';
import Dialog from '@mui/material/Dialog';
import MuiDialogContent from '@mui/material/DialogContent';
import MuiDialogTitle from '@mui/material/DialogTitle';
import IconButton from '@mui/material/IconButton';
import ListItemText from '@mui/material/ListItemText';
import MenuItem from '@mui/material/MenuItem';
import TextField from '@mui/material/TextField';
import Tooltip from '@mui/material/Tooltip';
import Typography from '@mui/material/Typography';
import { ReactComponent as DropArrow } from 'assets/DropArrow.svg';
import clearIcon from 'assets/clear.svg';
import alertInfo from 'assets/info.svg';
import saveIcon from 'assets/save.svg';
import saveIconDis from 'assets/saveDisable.svg';
import React, { useCallback, useEffect, useState } from 'react';
import { useIntl } from 'react-intl';
import { useDispatch } from 'react-redux';
import { toast } from 'react-toastify';
import { updateWellManagementModal } from '../../../../../redux/WellGuardian';
import {
  useCountryState,
  useSelectedSite,
  useWellData,
  useWellManagementModal,
} from '../../../../../redux/WellGuardian/selectors';
import wellGuardianApiClient from 'services/networking/wellGuardianApiClient';
import { CountryData, getAssetFromSite, WellInfo } from 'types/wellGuardian';

import styles from './WellManagement.module.scss';
import WellManagementPopUp from './WellManagementPopUp';
import { toNormalCase } from 'helpers/stringHelpers';

// Use type assertion to allow possibly missing properties
const WellManagement: React.FunctionComponent = () => {
  const classes = {
    requiredAsterisk: (styles as any).requiredAsterisk ?? 'required-asterisk',
    paper: (styles as any).paper ?? 'paper',
  } as const;
  const intl = useIntl();
  const dispatch = useDispatch();
  const modalOpen = useWellManagementModal();
  const [wellInfo, setWellInfo] = useState<any>({
    WELL_NAME: '',
    WELL_VIEW_ID: '',
    IDWELL: '',
    SLOT: '',
    TMORE_ID: '',
    ASSET: '',
    SITE: '',
  });
  const [piTagInfoWell, setPiTagInfoWell] = useState<any>({});
  const [schematicsInfoWell, setSchematicsInfoWell] = useState<any>({});
  const [copyWellInfo, setCopyWellInfo] = useState<any>({});
  const [isDataSame, setIsDataSame] = useState<boolean>(false);
  const [disablePI, setDisablePI] = useState<boolean>(false);
  const [disableSchematic, setDisableSchematic] = useState<boolean>(false);
  const fetchUserSite = useSelectedSite();
  const countrySelect = useCountryState();
  const asset = getAssetFromSite(fetchUserSite, countrySelect);
  const wellData = useWellData();
  const [allWellData, setAllWellData] = useState<WellInfo[]>([]);
  const [loading, setLoading] = useState<boolean>(modalOpen === 'update');
  const schematicTypeUK = [
    '5.04 Completion Schematic',
    '5.07 Wellhead and Trees Reports',
    '5.08 P&A Suspension Schematic',
  ];
  const schematicTypeNonUK = ['Completion Schematic', 'X-mas Tree', 'Well Barrier'];
  const annulusType = ['A-Annulus PI-Tag', 'B-Annulus PI-Tag', 'C-Annulus PI-Tag'];
  const [errorWellName, setErrorWellName] = useState<boolean>(false);
  const [errorWellId, setErrorWellId] = useState<boolean>(false);
  const [popup, setPopUp] = useState<boolean>(false);
  const [popupName, setPopupName] = useState<string>('');
  const [fetchData, setFetchData] = useState<any>([]);
  const [open, setOpen] = useState<boolean>(false);

  const [inputValue, setInputValue] = useState<string>('');
  const [schematicsType, setSchematicsType] = React.useState<any[]>([]);
  const [annulusTypes, setAnnulusTypes] = React.useState<any[]>([]);

  // Test harness: expose internal setters to facilitate deterministic selection in tests
  if (process.env.NODE_ENV === 'test') {
    (window as any).__WellManagementTestAPI = {
      setAnnulusTypes,
      setSchematicsType,
      setAllWellData: (data: any) => setAllWellData(data),
      setWellInfo: (updater: any) =>
        setWellInfo((prev: any) => ({
          ...prev,
          ...(typeof updater === 'function' ? updater(prev) : updater),
        })),
      setPiTagInfoWell: (updater: any) =>
        setPiTagInfoWell((prev: any) => ({
          ...prev,
          ...(typeof updater === 'function' ? updater(prev) : updater),
        })),
      setSchematicsInfoWell: (updater: any) =>
        setSchematicsInfoWell((prev: any) => ({
          ...prev,
          ...(typeof updater === 'function' ? updater(prev) : updater),
        })),
      triggerSchematicsAdd: (mode: string) => handleSchematicsAdd(mode),
    };
  }

  // Defensive: guard against unexpected undefined during isolated test shallow renders
  const errorWell =
    modalOpen === 'add' &&
    (allWellData ?? []).some(well => well.wellViewId === wellInfo.WELL_VIEW_ID);

  const wellInfoInputs = [
    {
      id: 'WELL_NAME',
      name: 'WELL NAME',
      tooltip: `Prompts the end User to add OPS Well Name in the Well Guardian. 
      Note: Select the OPS Well Name from the drop down list.`,
    },
    {
      id: 'WELL_VIEW_ID',
      name: 'WELL VIEW ID',
      tooltip: 'Verify the Well View / TUWI against UWIMS value',
    },
    {
      id: 'IDWELL',
      name: 'IDWELL',
      tooltip: '',
    },
    {
      id: 'TMORE_ID',
      name: 'TMORE ID',
      tooltip: 'Prompts the end User to enter the TMore Well_General-ID Num available with LBU',
    },
    {
      id: 'ASSET',
      name: 'ASSET',
      tooltip: '',
    },
    {
      id: 'SITE',
      name: 'SITE',
      tooltip: '',
    },
    {
      id: 'SLOT',
      name: 'SLOT',
      tooltip: 'Prompts the End User to enter the "SLOT" allocated for the Well Construction',
    },
  ];

  const piTagInfo = [
    {
      id: 'WELL_NAME',
      name: 'WELL NAME',
      tooltip: '',
    },
    {
      id: 'WELL_VIEW_ID',
      name: 'WELL VIEW ID',
      tooltip: '',
    },
    {
      id: 'WELL_STATUS_TAG',
      name: 'WELL STATUS TAG',
      tooltip: 'Prompts the end User to enter the "PI-Tag" available for the Well Status',
    },
    {
      id: 'ANNULUS_TYPE',
      name: 'ANNULUS TYPE',
      tooltip: 'Prompt User to select the Annulus for entering the respective Pi tag',
    },
  ];

  const compareWellInfo = useCallback(() => {
    const isSame = Object.keys(wellInfo).every((key: any) => wellInfo[key] === copyWellInfo[key]);
    setIsDataSame(isSame);
  }, [wellInfo, copyWellInfo]);

  useEffect(() => {
    compareWellInfo();
  }, [wellInfo, copyWellInfo, compareWellInfo]);

  useEffect(() => {
    if (modalOpen === 'add' && asset !== 'ALL' && fetchUserSite !== 'ALL') {
      setWellInfo({
        SITE: intl.formatMessage({ id: `wellGuardian.sites.${fetchUserSite}` }),
        ASSET: intl.formatMessage({ id: `wellGuardian.assets.${asset}` }),
      });
      setPiTagInfoWell({
        SITE: intl.formatMessage({ id: `wellGuardian.sites.${fetchUserSite}` }),
        ASSET: intl.formatMessage({ id: `wellGuardian.assets.${asset}` }),
      });
      setSchematicsInfoWell({
        SITE: intl.formatMessage({ id: `wellGuardian.sites.${fetchUserSite}` }),
        ASSET: intl.formatMessage({ id: `wellGuardian.assets.${asset}` }),
      });
    }
  }, [asset, fetchUserSite, intl, modalOpen]);

  useEffect(() => {
    if (wellInfo?.WELL_NAME !== '') {
      setPiTagInfoWell((prev: any) => ({
        ...prev,
        WELL_NAME: wellInfo.WELL_NAME,
      }));
      setSchematicsInfoWell((prev: any) => ({
        ...prev,
        WELL_NAME: wellInfo.WELL_NAME,
      }));
    }
    if (wellInfo?.WELL_VIEW_ID !== '') {
      setPiTagInfoWell((prev: any) => ({
        ...prev,
        WELL_VIEW_ID: wellInfo.WELL_VIEW_ID,
      }));
      setSchematicsInfoWell((prev: any) => ({
        ...prev,
        WELL_VIEW_ID: wellInfo.WELL_VIEW_ID,
      }));
    }

    if (wellInfo?.ASSET !== '') {
      setSchematicsInfoWell((prev: any) => ({
        ...prev,
        ASSET: wellInfo.ASSET,
      }));
    }
    if (wellInfo?.SITE !== '') {
      setSchematicsInfoWell((prev: any) => ({
        ...prev,
        SITE: wellInfo.SITE,
      }));
    }

    if (wellInfo?.WELL_NAME === '') {
      setPiTagInfoWell((prev: any) => ({
        ...prev,
        WELL_NAME: '',
      }));
      setSchematicsInfoWell((prev: any) => ({
        ...prev,
        WELL_NAME: '',
      }));
    }
    if (wellInfo?.WELL_VIEW_ID === '') {
      setPiTagInfoWell((prev: any) => ({
        ...prev,
        WELL_VIEW_ID: '',
      }));
      setSchematicsInfoWell((prev: any) => ({
        ...prev,
        WELL_VIEW_ID: '',
      }));
    }

    if (wellInfo?.ASSET === '') {
      setSchematicsInfoWell((prev: any) => ({
        ...prev,
        ASSET: '',
      }));
    }
    if (wellInfo?.SITE === '') {
      setSchematicsInfoWell((prev: any) => ({
        ...prev,
        SITE: '',
      }));
    }
  }, [wellInfo]);

  useEffect(() => {
    const fetchWellData = async () => {
      if (!countrySelect) return;

      try {
        const res = await wellGuardianApiClient.getWellsInfoForSite('ALL', 'ALL', countrySelect);
        setAllWellData(res);

        if (modalOpen === 'update' && wellData?.wellViewId) {
          setLoading(true);

          const getData = await wellGuardianApiClient.getWells(wellData.wellViewId);
          const getDataPi = await wellGuardianApiClient.getPiTagWells(
            countrySelect ?? '',
            wellData?.wellViewId ?? '',
          );
          const getDataLiveLink =
            countrySelect === CountryData.unitedKingdom
              ? await wellGuardianApiClient.getLiveLinkWells(wellData?.wellViewId ?? '')
              : null;
          const getDataSchematics =
            countrySelect !== CountryData.unitedKingdom
              ? await wellGuardianApiClient.getSharePointWells(wellData?.wellViewId ?? '')
              : null;

          const well = getData?.[0] || {};
          setWellInfo((prev: any) => ({
            ...prev,
            WELL_NAME: well.wellName,
            WELL_VIEW_ID: well.wellViewId,
            IDWELL: well.idWell,
            ASSET: well.asset,
            SITE: well.site,
            SLOT: well.slot,
            TMORE_ID: well.tmoreId,
          }));

          const dynamicTags = getDataPi?.reduce((tags, item) => {
            const mapping = {
              'A-annulus pressure': 'A-Annulus PI-Tag',
              'B-annulus pressure': 'B-Annulus PI-Tag',
              'C-annulus pressure': 'C-Annulus PI-Tag',
              'Well Status': 'WELL_STATUS_TAG',
            };

            if (item.attributeName && mapping[item.attributeName as keyof typeof mapping]) {
              const attributeName = item.attributeName as keyof typeof mapping;
              tags[mapping[attributeName] as keyof typeof tags] = item.tag;
            }

            return tags;
          }, {});

          setPiTagInfoWell((prev: any) => ({
            ...prev,
            ...dynamicTags,
            WELL_NAME: well.wellName,
            WELL_VIEW_ID: well.wellViewId,
          }));

          const tempAnnulusTypes = new Set();
          getDataPi?.forEach(item => {
            if (
              ['A-annulus pressure', 'B-annulus pressure', 'C-annulus pressure'].includes(
                item.attributeName ?? '',
              )
            ) {
              if (item.attributeName) {
                tempAnnulusTypes.add((item.attributeName as string).split('-')[0] + '-Annulus PI-Tag');
              }
            }
          });
          setAnnulusTypes(Array.from(tempAnnulusTypes));

          const tempSchematicsTypes = new Set();
          getDataLiveLink?.forEach(item => {
            if (
              [
                '5.04 Completion Schematic',
                '5.07 Wellhead and Trees Reports',
                '5.08 P&A Suspension Schematic',
              ].includes(item.schematicType ?? '')
            ) {
              tempSchematicsTypes.add(item.schematicType);
            }
          });
          getDataSchematics?.forEach(item => {
            if (
              item.schematicType &&
              ['Completion Schematic', 'X-mas Tree', 'Well Barrier'].includes(item.schematicType)
            ) {
              tempSchematicsTypes.add(item.schematicType);
            }
          });

          setSchematicsType(Array.from(tempSchematicsTypes));

          const liveLinkNameMapping: { [key: string]: string } = {
            '5.04 Completion Schematic': 'FOLDER NODE 5.04',
            '5.07 Wellhead and Trees Reports': 'FOLDER NODE 5.07',
            '5.08 P&A Suspension Schematic': 'FOLDER NODE 5.08',
            'Completion Schematic': 'COMPLETION SCHEMATIC URL',
            'X-mas Tree': 'X-MAS TREE URL',
            'Well Barrier': 'WELL BARRIER URL',
          };

          const setDynamicSchematic = (data: any) => {
            const tagInfo: { [key: string]: string } = {};
            data.forEach((item: any) => {
              if (liveLinkNameMapping[item.schematicType]) {
                tagInfo[liveLinkNameMapping[item.schematicType]] =
                  item.folderNode || item.sharepointPath || '';
              }
            });

            return tagInfo;
          };

          const dynamicSchematic = getDataLiveLink
            ? setDynamicSchematic(getDataLiveLink)
            : getDataSchematics
              ? setDynamicSchematic(getDataSchematics)
              : {};

          setSchematicsInfoWell((prev: any) => ({
            ...prev,
            ...dynamicSchematic,
            WELL_NAME: well.wellName,
            WELL_VIEW_ID: well.wellViewId,
            ASSET: well.asset,
            SITE: well.site,
          }));

          setLoading(false);
        }
      } catch {
        toast.error('Failed to fetch well data', {
          hideProgressBar: false,
          theme: 'colored',
        });
        setLoading(false);
      }
    };

    fetchWellData();
  }, [countrySelect, modalOpen, wellData?.country, wellData?.wellViewId]);

  useEffect(() => {
    if (errorWell) {
      toast.error('Well already exists', {
        hideProgressBar: false,
        theme: 'colored',
      });
    }
  }, [errorWell]);

  const handleWellInfoChange = (section: any, fieldId: any, value: any) => {
    switch (section) {
      case 'wellInfo':
        setWellInfo((prev: any) => ({ ...prev, [fieldId]: value ?? '' }));
        break;
      default:
        break;
    }
  };

  const areMandatoryFieldsWellInfo = () => {
    const { ASSET, IDWELL, SITE, SLOT, WELL_NAME, WELL_VIEW_ID } = wellInfo;

    return (
      ASSET && IDWELL && SITE && SLOT && WELL_NAME && WELL_VIEW_ID && !errorWell && !isDataSame
    );
  };

  const areMandatoryFieldsPiTag = () => {
    const { WELL_STATUS_TAG } = piTagInfoWell;

    const requiredField = annulusTypes.map(item => {
      return item;
    });

    return (
      // WELL_NAME &&
      // WELL_VIEW_ID &&
      WELL_STATUS_TAG &&
      annulusTypes.length > 0 &&
      !disablePI &&
      requiredField.every(field => piTagInfoWell[field])
    );
  };

  const areMandatoryFieldsSchematic = () => {
    // const { WELL_NAME, WELL_VIEW_ID, ASSET, SITE } = schematicsInfoWell;

    const requiredField =
      countrySelect === CountryData.unitedKingdom
        ? schematicsType.map(item => {
            const match = item.match(/^(\d+\.\d+)/);

            return match ? `FOLDER NODE ${match[0]}` : item;
          })
        : schematicsType.map(item => `${item.toUpperCase()} URL`);

    return (
      // WELL_NAME &&
      // WELL_VIEW_ID &&
      schematicsType.length > 0 &&
      !disableSchematic &&
      requiredField.every(field => schematicsInfoWell[field])
    );
  };

  const handleWellInfoAdd = useCallback(
    async (modalOpen: string) => {
      const data = {
        country: countrySelect,
        asset: wellInfo.ASSET,
        site: wellInfo.SITE,
        wellName: wellInfo.WELL_NAME,
        wellViewId: wellInfo.WELL_VIEW_ID,
        slot: wellInfo.SLOT,
        tmoreId: wellInfo.TMORE_ID,
        idWell: wellInfo.IDWELL,
      };

      if (modalOpen === 'add') {
        const wellData = await wellGuardianApiClient.createWells(data);
        if (wellData) {
          setCopyWellInfo({
            ASSET: wellInfo.ASSET,
            SITE: wellInfo.SITE,
            WELL_NAME: wellInfo.WELL_NAME,
            WELL_VIEW_ID: wellInfo.WELL_VIEW_ID,
            SLOT: wellInfo.SLOT,
            TMORE_ID: wellInfo.TMORE_ID,
            IDWELL: wellInfo.IDWELL,
          });
          toast.success('Well Created Successfully', {
            hideProgressBar: false,
            theme: 'colored',
          });
          const data = {
            pattern: `wg-well-info-${countrySelect}-${wellInfo.SITE}`,
          };
          await wellGuardianApiClient.clearCacheWells(data);
          setPopUp(false);
        } else {
          toast.error('Error in Creating WWell', {
            hideProgressBar: false,
            theme: 'colored',
          });
          setPopUp(false);
        }
      } else {
        const wellData = await wellGuardianApiClient.editWells(data);
        if (wellData) {
          toast.success('Well Updated Successfully', {
            hideProgressBar: false,
            theme: 'colored',
          });
          const data = {
            pattern: `wg-well-info-${countrySelect}-${wellInfo.SITE}`,
          };
          await wellGuardianApiClient.clearCacheWells(data);
          setPopUp(false);
        } else {
          toast.error('Error in Updating Well', {
            hideProgressBar: false,
            theme: 'colored',
          });
          setPopUp(false);
        }
      }
    },
    [countrySelect, wellInfo],
  );

  const handlePiTagAdd = useCallback(
    async (modalOpen: string) => {
      const data = {
        country: countrySelect,
        well: piTagInfoWell.WELL_NAME,
        wellViewId: piTagInfoWell.WELL_VIEW_ID,
        wellStatusTag: piTagInfoWell.WELL_STATUS_TAG,
        aAnnulus: piTagInfoWell['A-Annulus PI-Tag'] ?? '',
        bAnnulus: piTagInfoWell['B-Annulus PI-Tag'] ?? '',
        cAnnulus: piTagInfoWell['C-Annulus PI-Tag'] ?? '',
        idWell: wellInfo.IDWELL,
      };

      if (modalOpen === 'add') {
        const wellData = await wellGuardianApiClient.createWellsPI(data, countrySelect);
        if (wellData) {
          toast.success('PI Tag Created Successfully', {
            hideProgressBar: false,
            theme: 'colored',
          });
          setDisablePI(true);
          setPopUp(false);
        } else {
          toast.error('Error in Creating PI Tag', {
            hideProgressBar: false,
            theme: 'colored',
          });
          setPopUp(false);
        }
      } else {
        const wellData = await wellGuardianApiClient.editWellsPI(data, countrySelect);
        if (wellData) {
          toast.success('PI Tag Update Successfully', {
            hideProgressBar: false,
            theme: 'colored',
          });
          setPopUp(false);
        } else {
          toast.error('Error in Updating PI Tag', {
            hideProgressBar: false,
            theme: 'colored',
          });
          setPopUp(false);
        }
      }
    },
    [countrySelect, piTagInfoWell, wellInfo.IDWELL],
  );

  const handleSchematicsAdd = useCallback(
    async (modalOpen: string) => {
      if (countrySelect === CountryData.unitedKingdom) {
        const dataUK = {
          wellViewId: wellInfo.WELL_VIEW_ID,
          captureOptions: '',
          folderNode: '',
          schematicType: schematicsType,
          captureTime: new Date().toISOString(),
          node504: schematicsInfoWell['FOLDER NODE 5.04'] ?? '',
          node507: schematicsInfoWell['FOLDER NODE 5.07'] ?? '',
          node508: schematicsInfoWell['FOLDER NODE 5.08'] ?? '',
        };

        if (modalOpen === 'add') {
          const wellData = await wellGuardianApiClient.createWellsLiveUK(dataUK, countrySelect);
          if (wellData) {
            toast.success('Schematic Created Successfully', {
              hideProgressBar: false,
              theme: 'colored',
            });
            setDisableSchematic(true);
            setPopUp(false);
          } else {
            toast.error('Error in Creating Schematic', {
              hideProgressBar: false,
              theme: 'colored',
            });
            setPopUp(false);
          }
        } else {
          const wellData = await wellGuardianApiClient.editWellsLiveUK(dataUK, countrySelect);
          if (wellData) {
            toast.success('Schematic Updated Successfully', {
              hideProgressBar: false,
              theme: 'colored',
            });
            setPopUp(false);
          } else {
            toast.error('Error in Updating Schematic', {
              hideProgressBar: false,
              theme: 'colored',
            });
            setPopUp(false);
          }
        }
      } else {
        const dataNonUK = {
          country: countrySelect,
          asset: wellInfo.ASSET,
          wellName: wellInfo.WELL_NAME,
          wellViewId: wellInfo.WELL_VIEW_ID,
          site: wellInfo.SITE,
          completionUrl: schematicsInfoWell['COMPLETION SCHEMATIC URL'] ?? '',
          xmasTreeUrl: schematicsInfoWell['X-MAS TREE URL'] ?? '',
          wellBarrierUrl: schematicsInfoWell['WELL BARRIER URL'] ?? '',
          schematicType: schematicsType,
        };

        if (modalOpen === 'add') {
          const wellData = await wellGuardianApiClient.createWellsLive(dataNonUK);
          if (wellData) {
            toast.success('Schematic Created Successfully', {
              hideProgressBar: false,
              theme: 'colored',
            });
            setDisableSchematic(true);
            setPopUp(false);
          } else {
            toast.error('Error in Creating Schematic', {
              hideProgressBar: false,
              theme: 'colored',
            });
            setPopUp(false);
          }
        } else {
          const wellData = await wellGuardianApiClient.editWellsLive(dataNonUK, countrySelect);
          if (wellData) {
            toast.success('Schematic Updated Successfully', {
              hideProgressBar: false,
              theme: 'colored',
            });
            setPopUp(false);
          } else {
            toast.error('Error in Updating Schematic', {
              hideProgressBar: false,
              theme: 'colored',
            });
            setPopUp(false);
          }
        }
      }
    },
    [countrySelect, schematicsInfoWell, schematicsType, wellInfo],
  );

  const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const { value } = e.target;
    const updatedSchematicsType = typeof value === 'string' ? value.split(',') : value;
    const removedItems = schematicsType.filter(
      (item: string) => !updatedSchematicsType.includes(item),
    );
    setSchematicsInfoWell((prevState: any) => {
      const updatedInfoWell = { ...prevState };
      removedItems.forEach(item => {
        const key =
          countrySelect === CountryData.unitedKingdom
            ? `FOLDER NODE ${item.split(' ')[0]}`
            : `${item.toUpperCase()} URL`;
        delete updatedInfoWell[key];
      });

      return updatedInfoWell;
    });
    setSchematicsType(updatedSchematicsType);
  };

  const handleAnnulusChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const { value } = e.target;
    const updatedAnnulusType = typeof value === 'string' ? value.split(',') : value;
    const removedItems = schematicsType.filter(
      (item: string) => !updatedAnnulusType.includes(item),
    );
    setPiTagInfoWell((prevState: any) => {
      const updatedAnnulusType = { ...prevState };
      removedItems.forEach(item => {
        const key = item;
        delete updatedAnnulusType[key];
      });

      return updatedAnnulusType;
    });

    setAnnulusTypes(updatedAnnulusType);
  };

  const handleClickPopup = () => {
    if (popupName === 'Well Info') {
      handleWellInfoAdd(modalOpen);
    } else if (popupName === 'PI Tag') {
      handlePiTagAdd(modalOpen);
    } else if (popupName === 'Schematics') {
      handleSchematicsAdd(modalOpen);
    }
  };

  useEffect(() => {
    if (wellInfo.WELL_NAME !== '' || wellInfo.WELL_NAME !== undefined) {
      setErrorWellName(false);
    }
    if (wellInfo.WELL_VIEW_ID !== '' || wellInfo.WELL_VIEW_ID !== undefined) {
      setErrorWellId(false);
    }
  }, [wellInfo.WELL_NAME, wellInfo.WELL_VIEW_ID]);

  useEffect(() => {
    if (errorWellId && errorWellName) {
      toast.error('Please enter Well Name & Well View ID', {
        hideProgressBar: false,
        theme: 'colored',
      });
    }
    if (errorWellName && !errorWellId) {
      toast.error('Please enter Well Name', {
        hideProgressBar: false,
        theme: 'colored',
      });
    }
    if (errorWellId && !errorWellName) {
      toast.error('Please enter Well View ID', {
        hideProgressBar: false,
        theme: 'colored',
      });
    }
  }, [errorWellName, errorWellId]);

  useEffect(() => {
    const fetchWellData = async () => {
      const fetchWell = await wellGuardianApiClient.fetchWell(countrySelect);
      setFetchData(fetchWell);
    };

    fetchWellData();
  }, [countrySelect]);

  useEffect(() => {
    const well = (fetchData ?? []).find((well: any) => well.wellName === wellInfo.WELL_NAME);
    if (well) {
      setWellInfo((prev: any) => ({
        ...prev,
        WELL_NAME: well?.wellName,
        WELL_VIEW_ID: well?.wellViewId,
        IDWELL: well?.IDWELL,
      }));
    }
  }, [fetchData, wellInfo.WELL_NAME]);

  const filteredWells =
    wellInfo?.WELL_NAME !== undefined
      ? fetchData?.filter(
          (well: any) =>
            well?.wellName &&
            well?.wellName.toLowerCase()?.includes(wellInfo?.WELL_NAME?.toLowerCase()),
        )
      : fetchData?.filter((well: any) => well?.wellName !== null && well?.wellName !== undefined);

  return (
    <div>
      <Dialog
        aria-labelledby="well-management-dialog"
        open={modalOpen === 'add' || modalOpen === 'update'}
        maxWidth="xl"
        scroll="body"
        classes={{ paperWidthXl: styles.paperWidthXl, root: styles.root }}
        data-testid="well-management-dialog"
      >
        <MuiDialogTitle className={styles.titleDialog} component="div">
          <Typography
            component="span"
            variant="h6"
            className={styles.titleText}
            data-testid="dialog-title"
          >
            {intl.formatMessage({ id: 'wellGuardian.allWells.ModalTitle' })}
          </Typography>
          <IconButton
            aria-label="close"
            className={styles.closeButton}
            onClick={() => dispatch(updateWellManagementModal(''))}
            data-testid="close-button"
          >
            <CloseIcon />
          </IconButton>
        </MuiDialogTitle>
        <MuiDialogContent classes={{ root: styles.contentRoot }}>
          <div className={styles.wellCardGrid}>
            {loading ? (
              <div className={styles.wellGridCircular}>
                <CircularProgress size={50} color="inherit" />
              </div>
            ) : (
              <div className={styles.wellGrid}>
                <Typography variant="h6" className={styles.titleTexts}>
                  {intl.formatMessage({ id: 'wellGuardian.allWells.subHead1' })}
                </Typography>
                <div className={styles.wellContainer}>
                  <div className={styles.leftSide}>
                    {wellInfoInputs
                      .slice(0, Math.ceil(wellInfoInputs.length / 2))
                      ?.map((input, index) => (
                        <div className={styles.well} key={input.id}>
                          <div className={styles.right}>
                            {input.id === 'WELL_NAME' && modalOpen === 'add' ? (
                              <Autocomplete
                                freeSolo
                                value={wellInfo?.WELL_NAME ?? ''}
                                onChange={(e, newVal) => {
                                  // newVal can be string (freeSolo) or option object
                                  if (typeof newVal === 'string') {
                                    handleWellInfoChange('wellInfo', 'WELL_NAME', newVal || '');
                                    setInputValue(newVal || '');
                                    setOpen(false);
                                  } else if (newVal?.wellName) {
                                    handleWellInfoChange(
                                      'wellInfo',
                                      'WELL_NAME',
                                      newVal.wellName || '',
                                    );
                                    setInputValue(newVal.wellName || '');
                                    setOpen(false);
                                  }
                                }}
                                inputValue={inputValue}
                                onInputChange={(event, newInputValue) => {
                                  if (newInputValue !== null) {
                                    handleWellInfoChange(
                                      'wellInfo',
                                      'WELL_NAME',
                                      newInputValue || '',
                                    );
                                    setInputValue(newInputValue || '');
                                  }
                                }}
                                options={filteredWells}
                                getOptionLabel={(option: any) =>
                                  typeof option === 'string' ? option : option?.wellName || ''
                                }
                                isOptionEqualToValue={(option: any, value: any) =>
                                  typeof value === 'string'
                                    ? option?.wellName === value
                                    : option?.wellName === value?.wellName
                                }
                                openText="Open"
                                open={open}
                                onFocus={() => {
                                  setOpen(true);
                                }}
                                onBlur={() => {
                                  setOpen(false);
                                }}
                                slotProps={{
                                  paper: {
                                    sx: {
                                      width: 223,
                                      borderRadius: '10px',
                                      backgroundColor: '#f9f9f9',
                                      border: '1px solid #D0D3D4',
                                      boxShadow: '0px 4px 6px rgba(0, 0, 0, 0.1)',
                                      color: '#102741',
                                      fontSize: '14px',
                                      fontWeight: 400,
                                    },
                                  },
                                }}
                                renderInput={params => (
                                  <TextField
                                    {...params}
                                    label={intl.formatMessage({
                                      id: `wellGuardian.allWells.${
                                        input.id === 'WELL_NAME' ? 'WELL_NAME_OPS' : input.id
                                      }`,
                                    })}
                                    variant="outlined"
                                    size="small"
                                    margin="dense"
                                    required
                                    // Added for test stability: provide consistent test id for WELL_NAME field
                                    data-testid={`well-info-${input.id}`}
                                    slotProps={{
                                      input: {
                                        ...params.InputProps,
                                        className: styles.formInput,
                                        endAdornment: (
                                          <DropArrow
                                            style={{
                                              position: 'absolute',
                                              left: '175px',
                                              cursor: 'pointer',
                                            }}
                                            onClick={() => {
                                              setOpen((prev: any) => !prev);
                                            }}
                                          />
                                        ),
                                      },
                                      inputLabel: {
                                        className: styles.formLabel,
                                        classes: {
                                          asterisk: classes.requiredAsterisk,
                                        },
                                      },
                                    }}
                                    error={
                                      (input.id === 'WELL_VIEW_ID' && errorWell) ||
                                      (input.id === 'WELL_NAME' && errorWellName) ||
                                      (input.id === 'WELL_VIEW_ID' && errorWellId)
                                    }
                                  />
                                )}
                                noOptionsText="No Wells Found!"
                              />
                            ) : (
                              <TextField
                                id="outlined-lastHud-field"
                                label={intl.formatMessage({
                                  id: `wellGuardian.allWells.${
                                    input.id === 'WELL_NAME' ? 'WELL_NAME_OPS' : input.id
                                  }`,
                                })}
                                value={wellInfo[wellInfoInputs[index]?.id] ?? ''}
                                onChange={e =>
                                  handleWellInfoChange(
                                    'wellInfo',
                                    wellInfoInputs[index]?.id,
                                    e.target.value,
                                  )
                                }
                                slotProps={{
                                  input: {
                                    className: styles.formInput,
                                  },
                                  inputLabel: {
                                    className: styles.formLabel,
                                    classes: {
                                      asterisk: classes.requiredAsterisk,
                                    },
                                  },
                                  select: {
                                    className: styles.selectRoot,
                                  },
                                }}
                                variant="outlined"
                                size="small"
                                margin="dense"
                                required={input.id !== 'TMORE_ID'}
                                data-testid={`well-info-${input.id}`}
                                disabled={
                                  (modalOpen === 'update' && input.id === 'WELL_VIEW_ID') ||
                                  (modalOpen === 'update' && input.id === 'IDWELL') ||
                                  input.id === 'ASSET' ||
                                  input.id === 'SITE'
                                    ? true
                                    : false
                                }
                                error={
                                  (input.id === 'WELL_VIEW_ID' && errorWell) ||
                                  (input.id === 'WELL_NAME' && errorWellName) ||
                                  (input.id === 'WELL_VIEW_ID' && errorWellId)
                                }
                              />
                            )}
                            {input.tooltip !== '' ? (
                              <Tooltip
                                classes={{ tooltip: styles.tooltips }}
                                title={input.tooltip}
                                arrow={true}
                              >
                                <img src={alertInfo} className={styles.info} alt="alertInfo" />
                              </Tooltip>
                            ) : (
                              <div className={styles.empty} />
                            )}
                          </div>
                        </div>
                      ))}
                  </div>
                  <div className={styles.rightSide}>
                    {wellInfoInputs
                      .slice(Math.ceil(wellInfoInputs.length / 2))
                      ?.map((input, index) => (
                        <div className={styles.well} key={input.id}>
                          <div className={styles.right}>
                            <TextField
                              id="outlined-lastHud-field"
                              label={intl.formatMessage({
                                id: `wellGuardian.allWells.${input.id}`,
                              })}
                              value={
                                wellInfo[
                                  wellInfoInputs[index + Math.ceil(wellInfoInputs.length / 2)]?.id
                                ] ?? ''
                              }
                              onChange={e =>
                                handleWellInfoChange(
                                  'wellInfo',
                                  wellInfoInputs[index + Math.ceil(wellInfoInputs.length / 2)]?.id,
                                  e.target.value,
                                )
                              }
                              slotProps={{
                                input: {
                                  className: styles.formInput,
                                },
                                inputLabel: {
                                  className: styles.formLabel,
                                  classes: {
                                    asterisk: classes.requiredAsterisk,
                                  },
                                },
                                select: {
                                  className: styles.selectRoot,
                                },
                              }}
                              variant="outlined"
                              size="small"
                              margin="dense"
                              required={input.id !== 'TMORE_ID'}
                              data-testid={`well-info-${input.id}`}
                              disabled={
                                (modalOpen === 'update' && input.id === 'WELL_VIEW_ID') ||
                                (modalOpen === 'update' && input.id === 'IDWELL') ||
                                input.id === 'ASSET' ||
                                input.id === 'SITE'
                                  ? true
                                  : false
                              }
                              error={
                                (input.id === 'WELL_VIEW_ID' && errorWell) ||
                                (input.id === 'WELL_NAME' && errorWellName) ||
                                (input.id === 'WELL_VIEW_ID' && errorWellId)
                              }
                            />
                            {input.tooltip !== '' ? (
                              <Tooltip
                                classes={{ tooltip: styles.tooltips }}
                                title={input.tooltip}
                                arrow={true}
                              >
                                <img src={alertInfo} className={styles.info} alt="alertInfo" />
                              </Tooltip>
                            ) : (
                              <div className={styles.empty} />
                            )}
                          </div>
                        </div>
                      ))}
                  </div>
                </div>
                <div className={styles.buttonGroup}>
                  <Button
                    className={styles.linkButtonClr}
                    onClick={() => {
                      setWellInfo((prev: any) => ({
                        ...prev,
                        WELL_NAME: '',
                        WELL_VIEW_ID: '',
                        IDWELL: '',
                        SLOT: '',
                        TMORE_ID: '',
                        SITE: fetchUserSite,
                        ASSET: intl.formatMessage({ id: `wellGuardian.assets.${asset}` }),
                      }));
                      setInputValue('');
                    }}
                  >
                    <img src={clearIcon} className={styles.icon} alt="clearIcon" />
                    <span className={styles.linkButtonText}>
                      {intl.formatMessage({ id: 'wellGuardian.allWells.clear' })}
                    </span>
                  </Button>
                  <Button
                    className={styles.linkButton}
                    onClick={() => {
                      setPopUp(true);
                      setPopupName('Well Info');
                    }}
                    disabled={!areMandatoryFieldsWellInfo()}
                    data-testid="action-save-button"
                  >
                    <img
                      src={areMandatoryFieldsWellInfo() ? saveIcon : saveIconDis}
                      className={styles.icon}
                      alt="saveIcon"
                    />
                    <span className={styles.linkButtonText}>
                      {intl.formatMessage({ id: 'wellGuardian.allWells.save' })}
                    </span>
                  </Button>
                </div>
              </div>
            )}
            {loading ? (
              <div className={styles.wellGridCircular}>
                <CircularProgress size={50} color="inherit" />
              </div>
            ) : (
              <div className={styles.wellGrid}>
                <Typography variant="h6" className={styles.titleTexts}>
                  {intl.formatMessage({ id: 'wellGuardian.allWells.subHead2' })}
                </Typography>
                <div className={styles.well} data-testid="list-item">
                  <>
                    <div className={styles.right}>
                      <TextField
                        id="outlined-lastHud-field"
                        label={intl.formatMessage({
                          id: `wellGuardian.allWells.WELL_STATUS_TAG`,
                        })}
                        data-testid="pi-status-tag-input"
                        slotProps={{
                          input: {
                            className: styles.formInput,
                          },
                          inputLabel: {
                            className: styles.formLabel,
                            classes: {
                              asterisk: classes.requiredAsterisk,
                            },
                          },
                        }}
                        value={piTagInfoWell[piTagInfo[2]?.id] || ''}
                        onChange={e =>
                          setPiTagInfoWell((prev: any) => ({
                            ...prev,
                            [piTagInfo[2].id]: e.target.value ?? '',
                          }))
                        }
                        variant="outlined"
                        size="small"
                        margin="dense"
                        required
                      />
                    </div>
                  </>
                </div>
                <div className={styles.well} data-testid="list-item">
                  <div className={styles.right}>
                    <TextField
                      id="outlined-lastHud-field"
                      label={intl.formatMessage({
                        id: `wellGuardian.allWells.ANNULUS_TYPE`,
                      })}
                      data-testid="pi-annulus-type-select"
                      slotProps={{
                        input: {
                          className: styles.formInput,
                        },
                        inputLabel: {
                          className: styles.formLabel,
                          classes: {
                            asterisk: classes.requiredAsterisk,
                          },
                        },
                        select: {
                          multiple: true,
                          renderValue: (selected: any) => selected.join(', '),
                          IconComponent: (props: any) => (
                            <DropArrow {...props} style={{ right: '35px', top: '16px' }} />
                          ),
                          MenuProps: {
                            PaperProps: {
                              style: {
                                width: '223px',
                                borderRadius: '10px',
                                backgroundColor: '#f9f9f9',
                                border: '1px solid #D0D3D4',
                                boxShadow: '0px 4px 6px rgba(0, 0, 0, 0.1)',
                              },
                            },
                          },
                        },
                      }}
                      value={annulusTypes}
                      onChange={(e: any) => handleAnnulusChange(e)}
                      variant="outlined"
                      size="small"
                      margin="dense"
                      required
                      select={true}
                    >
                      {annulusType.map((item: any) => (
                        <MenuItem
                          dense
                          value={item}
                          key={item}
                          style={{
                            borderRadius: '10px',
                            backgroundColor: 'transparent',
                            textOverflow: 'ellipsis',
                            overflow: 'hidden',
                            whiteSpace: 'break-spaces',
                          }}
                        >
                          <Checkbox
                            checked={annulusTypes.includes(item)}
                            style={{
                              color: '#00ebc0e3',
                            }}
                          />
                          <ListItemText primary={item} />
                        </MenuItem>
                      ))}
                    </TextField>
                    <Tooltip
                      classes={{ tooltip: styles.tooltips }}
                      title="Prompt User to select the Annulus for entering the respective Pi tag"
                      arrow={true}
                    >
                      <img src={alertInfo} className={styles.info} alt="alertInfo" />
                    </Tooltip>
                  </div>
                </div>
                {annulusTypes.map(item => (
                  <div className={styles.well} key={item} data-testid="list-item">
                    <>
                      <div className={styles.right}>
                        <TextField
                          id="outlined-lastHud-field"
                          label={intl.formatMessage({
                            id: item,
                          })}
                          data-testid={`annulus-input-${item}`}
                          slotProps={{
                            input: {
                              className: styles.formInput,
                            },
                            inputLabel: {
                              className: styles.formLabel,
                              classes: {
                                asterisk: classes.requiredAsterisk,
                              },
                            },
                            select: {
                              className: styles.selectRoot,
                            },
                          }}
                          value={piTagInfoWell[item] || ''}
                          onChange={e =>
                            setPiTagInfoWell((prevState: any) => ({
                              ...prevState,
                              [item]: e.target.value,
                            }))
                          }
                          variant="outlined"
                          size="small"
                          margin="dense"
                          required
                        />
                        <Tooltip
                          classes={{ tooltip: styles.tooltips }}
                          title={`Prompts the end User to enter the "PI-Tag" available for the ${item}`}
                          arrow={true}
                        >
                          <img src={alertInfo} className={styles.info} alt="alertInfo" />
                        </Tooltip>
                      </div>
                    </>
                  </div>
                ))}
                <div className={styles.buttonGroup}>
                  <Button
                    className={styles.linkButtonClr}
                    onClick={() =>
                      setPiTagInfoWell({
                        SITE: fetchUserSite,
                        ASSET: intl.formatMessage({ id: `wellGuardian.assets.${asset}` }),
                      })
                    }
                  >
                    <img src={clearIcon} className={styles.icon} alt="clearIcon" />
                    <span className={styles.linkButtonText}>
                      {intl.formatMessage({ id: 'wellGuardian.allWells.clear' })}
                    </span>
                  </Button>
                  <Button
                    className={styles.linkButton}
                    onClick={() => {
                      if (wellInfo.WELL_NAME === undefined || wellInfo.WELL_NAME === '') {
                        setErrorWellName(true);
                      }
                      if (wellInfo.WELL_VIEW_ID === undefined || wellInfo.WELL_VIEW_ID === '') {
                        setErrorWellId(true);
                      }
                      if (
                        wellInfo.WELL_NAME !== undefined &&
                        wellInfo.WELL_NAME !== '' &&
                        wellInfo.WELL_VIEW_ID !== undefined &&
                        wellInfo.WELL_VIEW_ID !== ''
                      ) {
                        setPopUp(true);
                        setPopupName('PI Tag');
                      }
                    }}
                    disabled={!areMandatoryFieldsPiTag()}
                    data-testid="pi-tag-save-button"
                  >
                    <img
                      src={areMandatoryFieldsPiTag() ? saveIcon : saveIconDis}
                      alt="saveIcon"
                      className={styles.icon}
                    />
                    <span className={styles.linkButtonText}>
                      {intl.formatMessage({ id: 'wellGuardian.allWells.save' })}
                    </span>
                  </Button>
                </div>
              </div>
            )}
            {loading ? (
              <div className={styles.wellGridCircular}>
                <CircularProgress size={50} color="inherit" />
              </div>
            ) : (
              <div className={styles.wellGrid}>
                <Typography variant="h6" className={styles.titleTexts}>
                  {intl.formatMessage({ id: 'wellGuardian.allWells.subHead3' })}
                </Typography>
                <div className={styles.well} data-testid="list-item">
                  <div className={styles.right}>
                    <TextField
                      id="outlined-lastHud-field"
                      label={intl.formatMessage({
                        id: `wellGuardian.allWells.SCHEMATICS_TYPE`,
                      })}
                      data-testid="schematics-type-select"
                      slotProps={{
                        input: {
                          className: styles.formInput,
                        },
                        inputLabel: {
                          className: styles.formLabel,
                          classes: {
                            asterisk: classes.requiredAsterisk,
                          },
                        },
                        select: {
                          multiple: true,
                          renderValue: (selected: any) => selected.join(', '),
                          IconComponent: (props: any) => (
                            <DropArrow {...props} style={{ right: '35px', top: '16px' }} />
                          ),
                          MenuProps: {
                            PaperProps: {
                              style: {
                                width: '223px',
                                borderRadius: '10px',
                                backgroundColor: '#f9f9f9',
                                border: '1px solid #D0D3D4',
                                boxShadow: '0px 4px 6px rgba(0, 0, 0, 0.1)',
                              },
                            },
                          },
                        },
                      }}
                      value={schematicsType}
                      onChange={(e: any) => handleChange(e)}
                      variant="outlined"
                      size="small"
                      margin="dense"
                      required
                      select={true}
                    >
                      {countrySelect === CountryData.unitedKingdom
                        ? schematicTypeUK.map((item: any) => (
                            <MenuItem
                              dense
                              value={item}
                              key={item}
                              style={{
                                borderRadius: '10px',
                                backgroundColor: 'transparent',
                                textOverflow: 'ellipsis',
                                overflow: 'hidden',
                                whiteSpace: 'break-spaces',
                              }}
                            >
                              <Checkbox
                                checked={schematicsType.includes(item)}
                                style={{
                                  color: '#00ebc0e3',
                                }}
                              />
                              <ListItemText primary={item} />
                            </MenuItem>
                          ))
                        : schematicTypeNonUK.map((item: any) => (
                            <MenuItem dense value={item} key={item}>
                              <Checkbox
                                checked={schematicsType.includes(item)}
                                style={{
                                  color: '#00ebc0e3',
                                }}
                              />
                              <ListItemText primary={item} />
                            </MenuItem>
                          ))}
                    </TextField>
                    <Tooltip
                      classes={{ tooltip: styles.tooltips }}
                      title="Prompts the end User to Add Completion Schematics / Other documents like XMT, Well Barrier etc.,"
                      arrow={true}
                    >
                      <img src={alertInfo} className={styles.info} alt="alertInfo" />
                    </Tooltip>
                  </div>
                </div>
                {schematicsType.map(item => {
                  const keyId =
                    countrySelect === CountryData.unitedKingdom
                      ? `node-${item.split(' ')[0].replace('.', '_')}`
                      : `${item === 'Completion Schematic' ? 'COMPLETION_SCHEMATIC' : item.replace(/\s+/g, '_').toUpperCase()}_URL`;
                  console.log(item, 'item');
                  return (
                    <div className={styles.well} key={item} data-testid="list-item">
                      <>
                        <div className={styles.right}>
                          <TextField
                            id="outlined-lastHud-field"
                            label={intl.formatMessage({
                              id:
                                countrySelect === CountryData.unitedKingdom
                                  ? `Folder Node ${item.split(' ')[0]}`
                                  : `${
                                      item === 'Completion Schematic'
                                        ? 'Completion'
                                        : toNormalCase(item)
                                    } URL`,
                            })}
                            data-testid={`schematics-input-${keyId}`}
                            slotProps={{
                              input: {
                                className: styles.formInput,
                              },
                              inputLabel: {
                                className: styles.formLabel,
                                classes: {
                                  asterisk: classes.requiredAsterisk,
                                },
                              },
                            }}
                            value={
                              schematicsInfoWell[
                                countrySelect === CountryData.unitedKingdom
                                  ? `FOLDER NODE ${item.split(' ')[0]}`
                                  : `${item.toUpperCase()} URL`
                              ] || ''
                            }
                            onChange={e =>
                              setSchematicsInfoWell((prevState: any) => ({
                                ...prevState,
                                [countrySelect === CountryData.unitedKingdom
                                  ? `FOLDER NODE ${item.split(' ')[0]}`
                                  : `${item.toUpperCase()} URL`]: e.target.value,
                              }))
                            }
                            variant="outlined"
                            size="small"
                            margin="dense"
                            required
                            type={countrySelect === CountryData.unitedKingdom ? 'number' : 'text'}
                          />
                          <Tooltip
                            classes={{ tooltip: styles.tooltips }}
                            title={`Prompts the end User to enter the Sharepoint link for the ${item} folder for the Well`}
                            arrow={true}
                          >
                            <img src={alertInfo} className={styles.info} alt="alertInfo" />
                          </Tooltip>
                        </div>
                      </>
                    </div>
                  );
                })}
                <div className={styles.buttonGroup}>
                  <Button
                    className={styles.linkButtonClr}
                    onClick={() =>
                      setSchematicsInfoWell({
                        SITE: fetchUserSite,
                        ASSET: intl.formatMessage({ id: `wellGuardian.assets.${asset}` }),
                      })
                    }
                  >
                    <img src={clearIcon} className={styles.icon} alt="clearIcon" />
                    <span className={styles.linkButtonText}>
                      {intl.formatMessage({ id: 'wellGuardian.allWells.clear' })}
                    </span>
                  </Button>
                  <Button
                    className={styles.linkButton}
                    onClick={() => {
                      if (wellInfo.WELL_NAME === undefined || wellInfo.WELL_NAME === '') {
                        setErrorWellName(true);
                      }
                      if (wellInfo.WELL_VIEW_ID === undefined || wellInfo.WELL_VIEW_ID === '') {
                        setErrorWellId(true);
                      }
                      if (
                        wellInfo.WELL_NAME !== undefined &&
                        wellInfo.WELL_NAME !== '' &&
                        wellInfo.WELL_VIEW_ID !== undefined &&
                        wellInfo.WELL_VIEW_ID !== ''
                      ) {
                        setPopUp(true);
                        setPopupName('Schematics');
                      }
                    }}
                    disabled={!areMandatoryFieldsSchematic()}
                    data-testid="schematics-save-button"
                  >
                    <img
                      src={areMandatoryFieldsSchematic() ? saveIcon : saveIconDis}
                      alt="saveIcon"
                      className={styles.icon}
                    />
                    <span className={styles.linkButtonText}>
                      {intl.formatMessage({ id: 'wellGuardian.allWells.save' })}
                    </span>
                  </Button>
                </div>
              </div>
            )}
          </div>
        </MuiDialogContent>
      </Dialog>
      <WellManagementPopUp
        open={popup}
        onClose={() => setPopUp(false)}
        modalOpen={modalOpen}
        popupName={popupName}
        handleClickPopup={handleClickPopup}
      />
    </div>
  );
};

export default WellManagement;
